"use client"

import { Button, Grid, Stack, Box } from "@mui/material"
import { CancelButton } from "@src/components/Button"
import Icons from "@src/components/Icons"
import ProductCard from "@src/components/ProductCard"
import { ProductCardSkeleton } from "@src/components/ProductCard/ProductCardSkeleton"
import { API_URL, CONFIG_COLLECTION, blackFridayTextSx } from "@src/constants"
import EmptyProduct from "./empty-product"
import { useCallback, useState } from "react"
import { useLayoutContext } from "@src/layouts/provider"
import { useTranslation } from "react-i18next"
import fetchPublicClient from "@src/api/lib/fetch-public-client"
import HomeSectionContainer from "./home-section-container"
import { useAppRouter } from "@src/hooks/use-app-router"
import { startNavigationLoading } from "@src/utils/navigation-loading"
import UpcomingSaleProductsSection from "@src/sections/product-list/upcoming-sale-products-section"

const CategoryTitle = ({ children, active, onClick, isBlackFriday = false }) => {
  const activeStyle = {
    color: "black",
    fontWeight: 700,
    backgroundPosition: "100% 100%",
    backgroundSize: "100% 3px !important",
    "& svg": {
      opacity: 1
    }
  }

  return (
    <Button
      variant="text"
      disableRipple
      sx={{
        color: "rgba(0, 0, 0, 0.5)",
        fontSize: {
          xs: 14,
          md: 22
        },
        fontWeight: 400,
        lineHeight: "32px",
        minWidth: {
          xs: "auto",
          md: 150
        },
        border: "none",
        borderRadius: 0,
        width: "fit-content",
        padding: 0,
        textTransform: "none",
        whiteSpace: "nowrap",
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        paddingBottom: "3px",
        backgroundImage: isBlackFriday
          ? "linear-gradient(90deg, #E50914, #FF4500)"
          : "linear-gradient(#000 0 0)",
        backgroundPosition: active ? "100% 100%" : "0 100%",
        backgroundSize: active ? "100% 3px !important" : "0% 2px",
        backgroundRepeat: "no-repeat",
        transition: "background-size 0.3s, background-position 0s 0.3s",
        "& svg": {
          opacity: 0.5
        },
        "&:hover": {
          color: isBlackFriday ? "inherit" : "black",
          backgroundPosition: "100% 100%",
          backgroundSize: "100% 3px !important",
          backgroundColor: "transparent",
          "& svg": {
            opacity: 1
          },
          "& .bf-text": {
            transform: "scale(1.06)"
          }
        },
        ...(active && !isBlackFriday && activeStyle)
      }}
      onClick={onClick}
    >
      {isBlackFriday ? (
        <Box
          component="span"
          className="bf-text"
          sx={{
            ...blackFridayTextSx,
            transition: "transform 0.25s ease"
          }}
        >
          {children}
        </Box>
      ) : (
        children
      )}
    </Button>
  )
}

interface TabType {
  label: string
  url: string
  key: string
}

const ProductTabs = ({ products }) => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const { config } = useLayoutContext()
  const [tabActive, setTabActive] = useState<TabType>(
    config?.on_sale ? CONFIG_COLLECTION.ON_SALE : CONFIG_COLLECTION.NEW_IN
  )
  const [entries, setEntries] = useState(products)
  const [loading, setLoading] = useState(false)
  const [hasUpcomingSale, setHasUpcomingSale] = useState(false)

  const handleChangeTab = tab => async () => {
    setTabActive(tab)
    setLoading(true)
    loadProduct(tab.key)
  }

  const loadProduct = async type => {
    try {
      const response = await fetchPublicClient({
        method: "GET",
        url: API_URL.PAGINATE_PRODUCT,
        queries: {
          type: type
        }
      })
      setEntries(response.data.items)
    } catch (error) {
      console.error("Failed to load products:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleViewAll = useCallback(() => {
    if (!tabActive?.url) return
    startNavigationLoading()
    router.push(tabActive.url)
  }, [router, tabActive?.url])

  return (
    <HomeSectionContainer>
      <Stack
        mb={2}
        alignItems="center"
        spacing={{
          xs: 2,
          sm: 8,
          md: 14
        }}
        sx={{
          width: { xs: 0, md: "auto" },
          minWidth: { xs: "100%", md: 0 },
          overflowX: { xs: "auto", md: "visible" },
          justifyContent: { xs: "flex-start", md: "center" },
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none"
          }
        }}
      >
        {config?.on_sale && (
          <CategoryTitle
            key={CONFIG_COLLECTION.ON_SALE.key}
            active={tabActive?.key === CONFIG_COLLECTION.ON_SALE.key}
            onClick={handleChangeTab(CONFIG_COLLECTION.ON_SALE)}
            isBlackFriday
          >
            {t("on_sale")}
          </CategoryTitle>
        )}
        <CategoryTitle
          key={CONFIG_COLLECTION.NEW_IN.key}
          active={tabActive?.key === CONFIG_COLLECTION.NEW_IN.key}
          onClick={handleChangeTab(CONFIG_COLLECTION.NEW_IN)}
        >
          NEW ARRIVALS
        </CategoryTitle>
        <CategoryTitle
          key={CONFIG_COLLECTION.BEST_SELLER.key}
          active={tabActive?.key === CONFIG_COLLECTION.BEST_SELLER.key}
          onClick={handleChangeTab(CONFIG_COLLECTION.BEST_SELLER)}
        >
          {t("best_seller")}
        </CategoryTitle>
        {!config?.on_sale && (
          <CategoryTitle
            key={CONFIG_COLLECTION.ON_SALE.key}
            active={tabActive?.key === CONFIG_COLLECTION.ON_SALE.key}
            onClick={handleChangeTab(CONFIG_COLLECTION.ON_SALE)}
            isBlackFriday
          >
            {t("on_sale")}
          </CategoryTitle>
        )}
      </Stack>
      {tabActive?.key === CONFIG_COLLECTION.ON_SALE.key && (
        <UpcomingSaleProductsSection
          pageSize={9}
          carousel
          mobileCardDetails={false}
          onHasProductsChange={setHasUpcomingSale}
          onViewMore={handleViewAll}
        />
      )}
      <Grid
        container
        spacing={{
          xs: 1,
          md: 3
        }}
        sx={{
          marginLeft: {
            xs: "-8px",
            md: "-24px"
          },
          width: {
            xs: "calc(100% + 8px)",
            md: "calc(100% + 24px)"
          }
        }}
      >
        {loading ? (
          new Array(8).fill(0).map((_, index) => (
            <Grid key={index} item xs={6} lg={3}>
              <ProductCardSkeleton />
            </Grid>
          ))
        ) : entries?.length === 0 ? (
          hasUpcomingSale &&
          tabActive?.key === CONFIG_COLLECTION.ON_SALE.key ? null : (
            <EmptyProduct
              isOnSale={tabActive?.key === CONFIG_COLLECTION.ON_SALE.key}
            />
          )
        ) : (
          entries.map((product, index) => (
            <Grid key={index} item xs={6} lg={3}>
              <ProductCard
                key={product.id}
                product={product}
              />
            </Grid>
          ))
        )}
      </Grid>
      {(loading || entries?.length > 0) && (
        <CancelButton
          type="button"
          height="40px"
          label={t("view_all")}
          endIcon={<Icons name="arrow-right" />}
          minHeight="40px"
          sx={{
            margin: {
              xs: "24px auto 0",
              md: "48px auto 0"
            },
            minWidth: {
              xs: "220px !important",
              md: "328px !important"
            }
          }}
          onClick={handleViewAll}
        />
      )}
    </HomeSectionContainer>
  )
}

export default ProductTabs
